import React from 'react';
import { Box, Typography, IconButton, Tooltip, Grid } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TagIcon from '@mui/icons-material/Tag';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { QueueTile, SortOrder } from './QueueTile';
import { QueueCount } from 'hooks/useQueryDashboard';

interface QueueConfig {
    label: string;
    statusValue: string;
}

interface TransactionQueueSectionProps {
    queues: QueueConfig[];
    queueCounts: QueueCount[];
    loading: boolean;
    sortOrders: Record<string, SortOrder>;
    onSortChange: (statusValue: string, order: SortOrder) => void;
    onRefresh: () => void;
    onTileClick: (queueLabel: string, statusValue: string) => void;
    thresholdLow: number;
    thresholdHigh: number;
}

export const TransactionQueueSection: React.FC<TransactionQueueSectionProps> = ({
    queues,
    queueCounts,
    loading,
    sortOrders,
    onSortChange,
    onRefresh,
    onTileClick,
    thresholdLow,
    thresholdHigh,
}) => {
    const totalCount = queueCounts.reduce((sum, q) => sum + q.count, 0);

    const getCount = (statusValue: string): number | null => {
        if (loading) return null;
        const match = queueCounts.find(
            (q) => q.transactionStatus.toLowerCase() === statusValue.toLowerCase()
        );
        return match ? match.count : 0;
    };

    return (
        <Box
            data-testid="transaction-queue-section"
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                backgroundColor: 'background.default',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.default',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SyncAltIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: 'text.primary' }}>
                        Transaction Queue
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Refresh">
                        <IconButton
                            data-testid="transaction-queue-refresh-button"
                            size="small"
                            onClick={onRefresh}
                            aria-label="Refresh queue counts"
                        >
                            <RefreshIcon fontSize="small" sx={{ color: 'primary.main' }} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Total Transactions">
                        <Box
                            data-testid="transaction-queue-total-count"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                backgroundColor: 'primary.main',
                                borderRadius: 1,
                                px: 1,
                                py: 0.25,
                            }}
                        >
                            <TagIcon sx={{ color: 'common.white', fontSize: 14 }} />
                            <Typography sx={{ color: 'common.white', fontSize: '0.8rem', fontWeight: 600 }}>
                                {loading ? '...' : totalCount}
                            </Typography>
                        </Box>
                    </Tooltip>
                </Box>
            </Box>

            <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
                <Grid container spacing={2} data-testid="transaction-queue-tile-grid">
                    {queues.map((queue) => (
                        <Grid item xs={12} sm={6} key={queue.statusValue}>
                            <QueueTile
                                dataTestId={`queue-tile-${queue.statusValue.replace(/\s+/g, '-').toLowerCase()}`}
                                label={queue.label}
                                count={getCount(queue.statusValue)}
                                loading={loading}
                                sortOrder={sortOrders[queue.statusValue] ?? 'NEWEST'}
                                onSortChange={(order) => onSortChange(queue.statusValue, order)}
                                onTileClick={() => onTileClick(queue.label, queue.statusValue)}
                                thresholdLow={thresholdLow}
                                thresholdHigh={thresholdHigh}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Box>
    );
};